import { useCallback, useEffect, useRef, useState } from 'react';
import { API_DELETE, API_GET, API_POST, useConfig } from '../../api/api';
import usePersistedState from '../../global/usePersistedState';
import { useChatClient } from '../chat-system/useChatClient';
import { request } from './aiChatApi';
import { addAiGrant, aiGrantsFor } from './aiPermissionsCookie';

const BUSY_STATUSES = ['running', 'awaiting_approval', 'awaiting_budget'];
export const isBusyStatus = (status) => BUSY_STATUSES.includes(status);

// The websocket event is the primary signal; polling is the fallback when
// there is no socket, and a slow safety net for missed events otherwise.
const POLL_INTERVAL = 3000;
const POLL_INTERVAL_WITH_SOCKET = 15000;
const TITLE_LENGTH = 60;

// null (model default) is left out of the request
const effortPayload = (effort) => (effort ? { reasoningEffort: effort } : {});

function deriveTitle(prompt) {
  const firstLine =
    prompt
      .split('\n')
      .map((line) => line.trim())
      .find(Boolean) || '';
  return firstLine.length > TITLE_LENGTH
    ? `${firstLine.slice(0, TITLE_LENGTH - 1).trim()}…`
    : firstLine;
}

/**
 * State of the AI chat panel. Chats are scoped by project: on pages that belong
 * to a project only that project's chats are listed, elsewhere the chats
 * without a project. The last opened chat is remembered per scope.
 */
export function useAiChatSession({
  active,
  projectId,
  reasoningEffort = null,
  onTurnFinished,
}) {
  const config = useConfig();
  const { getEcho, connectionStatus } = useChatClient();

  const scopeKey = projectId ? `project:${projectId}` : 'general';
  const [activeChats, setActiveChats] = usePersistedState(
    'aiChat.activeChats',
    {},
  );

  const [chats, setChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [activeChatId, setActiveChatId] = useState(null);
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  // optimistic { chatId, message, knownIds } until the stored prompt shows up
  const [pending, setPending] = useState(null);
  const [budget, setBudget] = useState(undefined); // undefined = unknown
  const [error, setError] = useState(null);

  const activeChatIdRef = useRef(null);
  activeChatIdRef.current = activeChatId;
  const chatsRef = useRef(chats);
  chatsRef.current = chats;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const activeChatsRef = useRef(activeChats);
  activeChatsRef.current = activeChats;
  const onTurnFinishedRef = useRef(onTurnFinished);
  onTurnFinishedRef.current = onTurnFinished;
  const reasoningEffortRef = useRef(reasoningEffort);
  reasoningEffortRef.current = reasoningEffort;
  // a freshly created chat has no transcript yet, loading it would only race the send
  const skipLoadRef = useRef(null);

  const selectChat = useCallback(
    (chatId) => {
      setActiveChatId(chatId);
      setError(null);
      setActiveChats((prev) => {
        const next = { ...prev };
        if (chatId) next[scopeKey] = chatId;
        else delete next[scopeKey];
        return next;
      });
    },
    [scopeKey, setActiveChats],
  );

  // chats of the current scope
  useEffect(() => {
    if (!active || !config) return;
    let cancelled = false;

    setChats([]);
    setActiveChatId(null);
    skipLoadRef.current = null;
    setChatsLoading(true);

    const filter = projectId ? `&projectId[eq]=${projectId}` : '';
    request(config, `ai-chats?perPage=50${filter}`, API_GET)
      .then((res) => {
        if (cancelled) return;
        const list = (res.data || []).filter(
          (c) => projectId || c.projectId == null,
        );
        const remembered = activeChatsRef.current[scopeKey];
        setChats(list);
        setActiveChatId(
          list.some((c) => c.id === remembered)
            ? remembered
            : (list[0]?.id ?? null),
        );
      })
      .catch(() => {
        if (!cancelled) setChats([]);
      })
      .finally(() => {
        if (!cancelled) setChatsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [active, config, scopeKey, projectId]);

  const loadMessages = useCallback(
    (chatId) =>
      request(config, `ai-chats/${chatId}/messages?perPage=50`, API_GET).then(
        (res) => {
          if (activeChatIdRef.current !== chatId) return;
          const loadedChat = res.data?.chat;
          // newest first from the API
          const loaded = [...(res.data?.messages || [])].reverse();
          setMessages(loaded);
          if (loadedChat) setChat(loadedChat);
          setPending((p) => {
            if (p?.chatId !== chatId) return p;
            const stored = loaded.some(
              (m) =>
                m.role === 'user' &&
                m.content === p.message.content &&
                !p.knownIds.includes(m.id),
            );
            return stored || !isBusyStatus(loadedChat?.status) ? null : p;
          });
        },
      ),
    [config],
  );

  useEffect(() => {
    if (!activeChatId) {
      setChat(null);
      setMessages([]);
      return;
    }
    if (skipLoadRef.current === activeChatId) {
      skipLoadRef.current = null;
      return;
    }
    if (!config) return;

    setMessages([]);
    setChat(chatsRef.current.find((c) => c.id === activeChatId) || null);
    setMessagesLoading(true);
    loadMessages(activeChatId)
      .catch((err) => setError(err.message || 'error'))
      .finally(() => {
        if (activeChatIdRef.current === activeChatId)
          setMessagesLoading(false);
      });
  }, [activeChatId, config, loadMessages]);

  const loadBudget = useCallback(() => {
    if (!config) return;
    request(config, 'ai/budget', API_GET)
      .then((res) => setBudget(res.data ?? null))
      .catch(() => setBudget(undefined));
  }, [config]);

  useEffect(() => {
    if (active) loadBudget();
  }, [active, loadBudget]);

  // lastError: why the turn failed ({ reason, message }), null when it succeeded
  const handleTurnFinished = useCallback(
    (chatId, status, lastError = null) => {
      setChats((prev) => {
        const finished = prev.find((c) => c.id === chatId);
        if (!finished) return prev;
        const updated = {
          ...finished,
          status,
          lastError,
          lastMessageAt: new Date().toISOString(),
        };
        return [updated, ...prev.filter((c) => c.id !== chatId)];
      });
      setChat((prev) =>
        prev?.id === chatId ? { ...prev, status, lastError } : prev,
      );
      if (activeChatIdRef.current === chatId)
        loadMessages(chatId).catch(() => {});
      else setPending((p) => (p?.chatId === chatId ? null : p));
      loadBudget();
      onTurnFinishedRef.current?.({ chatId, status, lastError });
    },
    [loadMessages, loadBudget],
  );

  const chatStatus = chat?.status;
  const socketConnected = connectionStatus === 'connected' && !!getEcho?.();

  // realtime: turn finished
  useEffect(() => {
    const echo = getEcho?.();
    if (!echo || !activeChatId || connectionStatus !== 'connected') return;

    const channelName = `ai-chat.${activeChatId}`;
    echo
      .private(channelName)
      .listen('.turn.finished', (payload) =>
        handleTurnFinished(
          activeChatId,
          payload?.status || 'idle',
          payload?.lastError ?? null,
        ),
      );

    return () => echo.leave(channelName);
  }, [activeChatId, connectionStatus, getEcho, handleTurnFinished]);

  // fallback: poll while a turn is in flight. A turn that pauses to ask for
  // a permission stays busy (awaiting_approval), so any status change counts;
  // while waiting for the user the poll only watches for other participants.
  useEffect(() => {
    if (!config || !activeChatId || !isBusyStatus(chatStatus)) return;

    const slow = socketConnected || chatStatus === 'awaiting_approval';
    const interval = setInterval(
      () => {
        request(config, `ai-chats/${activeChatId}`, API_GET)
          .then((res) => {
            const status = res.data?.status;
            if (status && status !== chatStatus)
              handleTurnFinished(
                activeChatId,
                status,
                res.data?.lastError ?? null,
              );
          })
          .catch(() => {});
      },
      slow ? POLL_INTERVAL_WITH_SOCKET : POLL_INTERVAL,
    );

    return () => clearInterval(interval);
  }, [config, activeChatId, chatStatus, socketConnected, handleTurnFinished]);

  const createChat = useCallback(
    async (prompt) => {
      const res = await request(
        config,
        'ai-chats',
        API_POST({
          title: deriveTitle(prompt),
          ...(projectId ? { projectId } : {}),
        }),
      );
      const created = res.data;
      skipLoadRef.current = created.id;
      activeChatIdRef.current = created.id;
      setChats((prev) => [created, ...prev]);
      setChat(created);
      setMessages([]);
      selectChat(created.id);
      return created.id;
    },
    [config, projectId, selectChat],
  );

  /**
   * Starts a turn. Creates the chat first when none is selected.
   * projectFiles: [{ projectId, path }] from the project network storage.
   * Rejects with the API error so the composer can restore its input.
   */
  const send = useCallback(
    async ({
      prompt,
      references = [],
      attachmentIds = [],
      projectFiles = [],
      localAttachments = [],
    }) => {
      setError(null);
      let chatId = activeChatIdRef.current;

      try {
        if (!chatId) chatId = await createChat(prompt);
      } catch (err) {
        setError(err.message || 'error');
        throw err;
      }

      setPending({
        chatId,
        knownIds: messagesRef.current.map((m) => m.id),
        message: {
          id: `local-${Date.now()}`,
          local: true,
          role: 'user',
          content: prompt,
          status: 'completed',
          createdAt: new Date().toISOString(),
          attachments: [],
          localReferences: references,
          localAttachments,
        },
      });

      try {
        const res = await request(
          config,
          `ai-chats/${chatId}/messages`,
          API_POST({
            prompt,
            attachments: attachmentIds,
            references: references.map(({ type, id }) => ({ type, id })),
            // files in project network storage, read by the backend itself
            ...(projectFiles.length ? { projectFiles } : {}),
            // what this browser granted the AI (capped by the backend)
            grantedPermissions: aiGrantsFor(chatId),
            ...effortPayload(reasoningEffortRef.current),
          }),
        );
        const status = res.data?.status || 'running';
        setChat((prev) => (prev?.id === chatId ? { ...prev, status } : prev));
        setChats((prev) =>
          prev.map((c) => (c.id === chatId ? { ...c, status } : c)),
        );
      } catch (err) {
        setPending((p) => (p?.chatId === chatId ? null : p));
        if (err.httpStatus === 409) {
          const status = err.data?.status || 'running';
          setChat((prev) =>
            prev?.id === chatId ? { ...prev, status } : prev,
          );
        }
        // 429: the budget refused the prompt, the banner should say so
        if (err.httpStatus === 429) loadBudget();
        setError(err.message || 'error');
        throw err;
      }
    },
    [config, createChat, loadBudget],
  );

  /**
   * Answers the permission requests of a paused turn and resumes it.
   * decisions: [{ id, decision: 'approve' | 'reject', scope?: 'once' | 'chat' | 'always', permissionKeys }]
   * Chat and "always" approvals are remembered in the cookie first (every
   * key the call needs), so the resumed turn and all later prompts carry them.
   */
  const decideApprovals = useCallback(
    async (decisions) => {
      const chatId = activeChatIdRef.current;
      if (!chatId) return;
      setError(null);
      decisions.forEach(({ decision, scope, permissionKeys }) => {
        if (decision !== 'approve' || scope === 'once') return;
        (permissionKeys || []).forEach((key) =>
          addAiGrant(key, scope === 'chat' ? chatId : null),
        );
      });
      try {
        const res = await request(
          config,
          `ai-chats/${chatId}/approvals`,
          API_POST({
            decisions: decisions.map(({ id, decision, scope }) => ({
              id,
              decision,
              ...(scope ? { scope } : {}),
            })),
            grantedPermissions: aiGrantsFor(chatId),
            // the resumed turn keeps thinking the way the user chose
            ...effortPayload(reasoningEffortRef.current),
          }),
        );
        const status = res.data?.status || 'running';
        setChat((prev) =>
          prev?.id === chatId ? { ...prev, status, pendingApprovals: [] } : prev,
        );
        setChats((prev) =>
          prev.map((c) => (c.id === chatId ? { ...c, status } : c)),
        );
      } catch (err) {
        // 409: someone else decided first, the transcript tells the rest
        if (err.httpStatus === 409) loadMessages(chatId).catch(() => {});
        if (err.httpStatus === 429) loadBudget();
        setError(err.message || 'error');
        throw err;
      }
    },
    [config, loadMessages, loadBudget],
  );

  const deleteChat = useCallback(
    async (chatId) => {
      await request(config, `ai-chats/${chatId}`, API_DELETE());
      const rest = chatsRef.current.filter((c) => c.id !== chatId);
      setChats(rest);
      if (activeChatIdRef.current === chatId) selectChat(rest[0]?.id ?? null);
    },
    [config, selectChat],
  );

  const newChat = useCallback(() => selectChat(null), [selectChat]);

  const chatMeta = chats.find((c) => c.id === activeChatId);

  return {
    chats,
    chatsLoading,
    activeChatId,
    // the list carries creator/participants, the transcript the fresh status
    chat: chat ? { ...chatMeta, ...chat } : null,
    messages,
    messagesLoading,
    pendingMessage:
      pending && pending.chatId === activeChatId ? pending.message : null,
    isBusy: isBusyStatus(chat?.status),
    // tool calls of a paused turn waiting for the user's permission
    pendingApprovals:
      chat?.status === 'awaiting_approval' ? chat.pendingApprovals || [] : [],
    budget,
    error,
    clearError: () => setError(null),
    selectChat,
    newChat,
    send,
    decideApprovals,
    deleteChat,
  };
}
